(() => {
  const selectors = {
    open: '[data-cart-product-update-open]',
    close: '[data-cart-product-update-close]',
    template: '[data-cart-product-update-template]',
    content: '[data-cart-product-update-content]',
    apiContent: '[data-api-content]',
    addButton: '[data-add-to-cart]',
    form: 'form[data-product-form]',
    properties: '[data-cart-line-properties]',
    sellingPlan: '[data-cart-line-selling-plan]',
  };

  class CartProductUpdate extends HTMLElement {
    connectedCallback() {
      this.openButton = this.querySelector(selectors.open);
      if (!this.openButton || this.openButton.dataset.cartProductUpdateBound === 'true') return;

      this.openButton.dataset.cartProductUpdateBound = 'true';
      this.openButton.addEventListener('click', () => this.open());
    }

    async open() {
      if (this.loading) return;

      this.loading = true;
      this.openButton.disabled = true;
      this.openButton.classList.add('is-loading');

      try {
        this.createModal();
        this.modal.classList.add('is-loading');
        this.showModal();

        const root = window.theme?.routes?.root || '/';
        const handle = encodeURIComponent(this.dataset.productHandle);
        const variantId = encodeURIComponent(this.dataset.variantId);
        const originalSellingPlan = this.readJson(selectors.sellingPlan, null);
        const params = new URLSearchParams({variant: variantId, section_id: 'api-product-upsell'});
        if (originalSellingPlan) params.set('selling_plan', originalSellingPlan);
        const response = await fetch(`${root}products/${handle}?${params.toString()}`);

        if (!response.ok) throw await this.responseError(response, 'product options load');

        const responseDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
        const apiContent = responseDocument.querySelector(selectors.apiContent);
        if (!apiContent) throw new Error('Product options could not be loaded.');

        this.modal.querySelector(selectors.content).innerHTML = apiContent.innerHTML;
        this.prepareForm();
        this.modal.querySelectorAll('[data-animation]').forEach((element) => element.classList.add('is-animated'));
        this.modal.classList.remove('is-loading');
      } catch (error) {
        this.showError(error.message || 'Product options could not be loaded.');
        this.modal?.classList.remove('is-loading');
      } finally {
        this.loading = false;
        this.openButton.disabled = false;
        this.openButton.classList.remove('is-loading');
      }
    }

    createModal() {
      this.close(false);

      const template = this.querySelector(selectors.template);
      if (!template) throw new Error('The product update dialog is missing.');

      this.modal = template.content.firstElementChild.cloneNode(true);
      document.body.appendChild(this.modal);

      this.modal.querySelector(selectors.close).addEventListener('click', () => this.close());
      this.modal.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.modal.addEventListener('click', (event) => {
        if (event.target === this.modal) this.close();
      });
    }

    showModal() {
      this.openButton.setAttribute('aria-expanded', 'true');
      this.modal.removeAttribute('inert');

      if (typeof this.modal.showModal === 'function') {
        this.modal.showModal();
      } else {
        this.modal.setAttribute('open', '');
      }

      document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true}));
    }

    prepareForm() {
      const form = this.modal.querySelector(selectors.form);
      const button = this.modal.querySelector(selectors.addButton);
      if (!form || !button) throw new Error('The product update form is missing.');

      // Broadcast creates ProductFormReader with the section container and later
      // expects `productForm.element.form`; provide that missing form reference.
      Object.defineProperty(this.modal, 'form', {
        value: form,
        configurable: true,
      });

      this.prepareMediaThumbnails();

      this.modal.addEventListener('click', (event) => {
        const paymentTrigger = event.target.closest('[uk-toggle*="#product-pay-modal"]');
        if (paymentTrigger && this.modal.contains(paymentTrigger)) this.lowerForPaymentModal();
      });

      const setUpdateButtonText = () => {
        const buttonText = button.querySelector('[data-add-to-cart-text]');
        if (buttonText && !button.disabled) buttonText.textContent = 'UPDATE';
      };
      setUpdateButtonText();

      // Variant changes update the theme button text to "Add to cart". Restore
      // the cart-specific action after the theme finishes handling the change.
      this.modal.addEventListener('change', () => requestAnimationFrame(setUpdateButtonText));

      // Capture before the theme's ProductForm click handler, which normally adds
      // another cart item. This dialog replaces the current cart line instead.
      this.modal.addEventListener(
        'click',
        (event) => {
          const submitButton = event.target.closest(selectors.addButton);
          if (!submitButton || !this.modal.contains(submitButton)) return;

          event.preventDefault();
          event.stopPropagation();
          this.updateLine(form, submitButton);
        },
        true
      );

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.updateLine(form, button);
      });
    }

    prepareMediaThumbnails() {
      const media = this.modal.querySelector('product-images');
      const mediaList = media?.querySelector('[data-product-media-list]');
      const slides = mediaList ? Array.from(mediaList.querySelectorAll('[data-media-id]')) : [];
      if (!media || !mediaList || slides.length < 2) return;

      const thumbnails = document.createElement('div');
      thumbnails.className = 'cart-product-update-modal__thumbnails';
      thumbnails.setAttribute('aria-label', 'Product media thumbnails');

      const scrollMediaIntoView = (mediaId) => {
        if (!window.matchMedia('(max-width: 749px)').matches) return;

        const activeSlide = slides.find((slide) => slide.dataset.mediaId === mediaId);
        if (!activeSlide) return;

        requestAnimationFrame(() => {
          mediaList.scrollTo({left: activeSlide.offsetLeft, behavior: 'smooth'});
        });
      };

      const updateActiveThumbnail = (mediaId) => {
        let activeThumbnail = null;

        thumbnails.querySelectorAll('[data-cart-product-update-thumbnail]').forEach((button) => {
          const isActive = button.dataset.thumbnailMediaId === mediaId;
          button.setAttribute('aria-current', String(button.dataset.thumbnailMediaId === mediaId));
          if (isActive) activeThumbnail = button;
        });

        if (!activeThumbnail) return;

        requestAnimationFrame(() => {
          activeThumbnail.scrollIntoView({block: 'nearest', inline: 'nearest', behavior: 'smooth'});
        });
      };

      slides.forEach((slide, index) => {
        const preview = slide.querySelector('img');
        if (!preview) return;

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cart-product-update-modal__thumbnail';
        button.dataset.cartProductUpdateThumbnail = '';
        button.dataset.thumbnailMediaId = slide.dataset.mediaId;
        button.setAttribute('aria-label', `View product media ${index + 1}`);
        button.setAttribute('aria-current', String(slide.classList.contains('media--active')));

        const thumbnailImage = preview.cloneNode(false);
        thumbnailImage.removeAttribute('srcset');
        thumbnailImage.removeAttribute('sizes');
        thumbnailImage.removeAttribute('loading');
        thumbnailImage.alt = '';
        button.appendChild(thumbnailImage);

        button.addEventListener('click', () => {
          slide.dispatchEvent(
            new CustomEvent('theme:media:select', {
              bubbles: true,
              detail: {id: slide.dataset.mediaId},
            })
          );
          updateActiveThumbnail(slide.dataset.mediaId);
        });

        thumbnails.appendChild(button);
      });

      if (!thumbnails.children.length) return;

      media.insertBefore(thumbnails, mediaList);

      const arrows = document.createElement('div');
      arrows.className = 'cart-product-update-modal__media-arrows';

      ['previous', 'next'].forEach((direction) => {
        const arrow = document.createElement('button');
        arrow.type = 'button';
        arrow.className = `cart-product-update-modal__media-arrow cart-product-update-modal__media-arrow--${direction}`;
        arrow.setAttribute('aria-label', direction === 'previous' ? 'Previous' : 'Next');
        arrow.addEventListener('click', () => {
          const activeIndex = slides.findIndex((slide) => slide.classList.contains('media--active'));
          const offset = direction === 'previous' ? -1 : 1;
          const targetIndex = (Math.max(activeIndex, 0) + offset + slides.length) % slides.length;
          const targetSlide = slides[targetIndex];

          targetSlide.dispatchEvent(
            new CustomEvent('theme:media:select', {
              bubbles: true,
              detail: {id: targetSlide.dataset.mediaId},
            })
          );
        });
        arrows.appendChild(arrow);
      });

      media.appendChild(arrows);
      media.addEventListener('theme:media:select', (event) => {
        if (event.detail?.id) {
          const mediaId = String(event.detail.id);
          updateActiveThumbnail(mediaId);
          scrollMediaIntoView(mediaId);
        }
      });
    }

    lowerForPaymentModal() {
      const paymentModal = document.getElementById('product-pay-modal');
      const cartModal = this.modal;
      if (!paymentModal || !cartModal?.open || cartModal.classList.contains('is-below-payment-modal')) return;

      cartModal.close();
      cartModal.classList.add('is-below-payment-modal');
      cartModal.setAttribute('open', '');

      paymentModal.addEventListener(
        'hidden',
        () => {
          if (this.modal !== cartModal || !cartModal.isConnected || !cartModal.open) return;

          cartModal.close();
          cartModal.classList.remove('is-below-payment-modal');

          if (typeof cartModal.showModal === 'function') {
            cartModal.showModal();
          } else {
            cartModal.setAttribute('open', '');
          }
        },
        {once: true}
      );
    }

    async updateLine(form, button) {
      if (this.updating || button.disabled) return;
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      const formData = new FormData(form);
      const newVariantId = String(formData.get('id') || '');
      const oldVariantId = String(this.dataset.variantId || '');
      if (!newVariantId) return this.showError('Please select an available option.');

      const originalSellingPlan = this.readJson(selectors.sellingPlan, null);
      const hasSellingPlanSelector = Boolean(form.querySelector('[name="selling_plan"]'));
      const selectedSellingPlan = formData.get('selling_plan');
      const desiredSellingPlan = hasSellingPlanSelector ? selectedSellingPlan || null : originalSellingPlan;
      const sellingPlanChanged = String(desiredSellingPlan || '') !== String(originalSellingPlan || '');

      if (newVariantId === oldVariantId && !sellingPlanChanged) {
        this.close();
        return;
      }

      this.updating = true;
      button.disabled = true;
      button.classList.add('is-loading');
      this.clearError();

      try {
        const properties = this.normalizeProperties(this.readJson(selectors.properties, {}));
        const root = window.theme?.routes?.root || '/';

        if (newVariantId === oldVariantId) {
          const changeResponse = await fetch(`${root}cart/change.js`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json', Accept: 'application/json'},
            body: JSON.stringify({
              line: Number(this.dataset.lineIndex),
              quantity: Number(this.dataset.lineQuantity) || 1,
              selling_plan: desiredSellingPlan ? Number(desiredSellingPlan) : null,
            }),
          });
          if (!changeResponse.ok) throw await this.responseError(changeResponse, 'selling plan update');

          this.finishUpdate();
          return;
        }

        // Submit the same FormData shape as the theme's working Add to Cart
        // flow. Shopify serializes empty Liquid properties as [], while the JSON
        // Cart API requires an object; FormData also preserves product form fields.
        const addData = new FormData(form);
        addData.set('id', newVariantId);
        addData.set('quantity', String(Number(this.dataset.lineQuantity) || 1));
        Object.entries(properties).forEach(([key, value]) => {
          addData.set(`properties[${key}]`, value);
        });

        if (desiredSellingPlan) {
          addData.set('selling_plan', String(desiredSellingPlan));
        } else {
          addData.delete('selling_plan');
        }

        const addResponse = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {Accept: 'application/json'},
          body: addData,
        });
        if (!addResponse.ok) throw await this.responseError(addResponse, 'variant add');
        const addResult = await addResponse.json();
        const addedLine = addResult.items?.[0] || addResult;

        const changeResponse = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({id: this.dataset.lineKey, quantity: 0}),
        });
        if (!changeResponse.ok) {
          const removalError = await this.responseError(changeResponse, 'original line removal');
          await this.rollbackAddedLine(root, addedLine);
          throw removalError;
        }

        this.finishUpdate();
      } catch (error) {
        this.showError(error.message || 'The cart could not be updated. Please try again.');
      } finally {
        this.updating = false;
        button.disabled = false;
        button.classList.remove('is-loading');
      }
    }

    readJson(selector, fallback) {
      const element = this.querySelector(selector);
      if (!element) return fallback;

      try {
        return JSON.parse(element.textContent);
      } catch (error) {
        return fallback;
      }
    }

    normalizeProperties(properties) {
      if (Array.isArray(properties)) {
        return properties.reduce((result, property) => {
          if (Array.isArray(property) && property.length >= 2) result[property[0]] = property[1];
          return result;
        }, {});
      }

      return properties && typeof properties === 'object' ? properties : {};
    }

    async rollbackAddedLine(root, addedLine) {
      if (!addedLine?.key) return;

      const addedQuantity = Number(this.dataset.lineQuantity) || 1;
      const rollbackQuantity = Math.max((Number(addedLine.quantity) || addedQuantity) - addedQuantity, 0);
      const request = () =>
        fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({id: addedLine.key, quantity: rollbackQuantity}),
        });

      let response = await request();
      if (response.status === 429) {
        await new Promise((resolve) => setTimeout(resolve, 600));
        response = await request();
      }
    }

    finishUpdate() {
      this.close();
      document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles: true}));
    }

    async responseError(response, action = 'cart update') {
      const fallback = `${action} failed (${response.status}). Please try again.`;

      try {
        const responseText = await response.text();
        const data = JSON.parse(responseText);
        return new Error(data.description || data.message || fallback);
      } catch (error) {
        return new Error(fallback);
      }
    }

    showError(message) {
      const container = this.modal?.querySelector('[data-cart-errors-container]') || this.modal?.querySelector(selectors.content);
      if (!container) return;

      let error = container.querySelector('[data-cart-product-update-error]');
      if (!error) {
        error = document.createElement('div');
        error.className = 'errors cart-product-update-modal__error';
        error.dataset.cartProductUpdateError = '';
        container.prepend(error);
      }
      error.textContent = message;
    }

    clearError() {
      this.modal?.querySelector('[data-cart-product-update-error]')?.remove();
    }

    close(restoreFocus = true) {
      if (!this.modal) return;

      if (this.modal.open && typeof this.modal.close === 'function') this.modal.close();
      this.modal.remove();
      this.modal = null;
      this.openButton?.setAttribute('aria-expanded', 'false');

      if (!window.theme?.hasOpenModals?.()) {
        document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
      }
      if (restoreFocus) this.openButton?.focus();
    }
  }

  if (!customElements.get('cart-product-update')) {
    customElements.define('cart-product-update', CartProductUpdate);
  }
})();
